import {
  MAX_PLAYERS,
  MIN_PLAYERS,
  Phase,
  type Player,
  PlayerStatus,
  type Room,
} from '@infiltre/shared';

/**
 * Pure preconditions mirroring the engine guards in `packages/shared/src/game/engine.ts`.
 * UI calls these to disable buttons *before* the server rejects with an Error.
 * Engine remains the authority — these are an optimistic UX layer that
 * eliminates the noisy "wrong_phase"/"only_host" toast loop from stale clicks.
 *
 * Each predicate must stay aligned with its engine counterpart. If a guard
 * changes server-side, update the same predicate here. Drift = UX regression
 * (buttons stay enabled past the deadline) or false-disable (buttons greyed
 * when the action would succeed).
 */

const isAlive = (p: Player): boolean => p.status === PlayerStatus.Alive;

const findMe = (room: Room, playerId: string | null): Player | null => {
  if (!playerId) return null;
  return room.players.find((p) => p.id === playerId) ?? null;
};

/** Engine: StartGame requires host + Lobby + [MIN..MAX] players. */
export const canStart = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.Lobby) return false;
  const me = findMe(room, playerId);
  if (!me?.isHost) return false;
  const count = room.players.length;
  return count >= MIN_PLAYERS && count <= MAX_PLAYERS;
};

/** Engine: OpenVote requires host + Discuss phase. */
export const canOpenVote = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.Discuss) return false;
  const me = findMe(room, playerId);
  return me?.isHost === true;
};

/** Engine: CastVote requires Vote phase + alive voter + alive target ≠ voter + not already voted. */
export const canCastVote = (
  room: Room | null,
  voterId: string | null,
  targetId: string,
): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.Vote) return false;
  const voter = findMe(room, voterId);
  if (!voter || !isAlive(voter)) return false;
  if (voter.id === targetId) return false;
  const target = room.players.find((p) => p.id === targetId);
  if (!target || !isAlive(target)) return false;
  return !room.votes.some((v) => v.voter === voterId);
};

/** Engine: EliminatePlayer requires host + Discuss|Vote + alive target. */
export const canEliminate = (
  room: Room | null,
  playerId: string | null,
  targetId: string,
): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.Discuss && room.phase !== Phase.Vote) return false;
  const me = findMe(room, playerId);
  if (!me?.isHost) return false;
  const target = room.players.find((p) => p.id === targetId);
  return target ? isAlive(target) : false;
};

/** Engine: ClochardGuess requires ClochardGuess phase + actor is the eliminated player. */
export const canClochardGuess = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.ClochardGuess) return false;
  if (!playerId) return false;
  return room.eliminatedThisRound === playerId;
};

/** Engine: NextRound (host-driven advance) requires host + RevealVote phase (or Finished rematch). */
export const canAdvanceFromReveal = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.RevealVote && room.phase !== Phase.Finished) return false;
  const me = findMe(room, playerId);
  return me?.isHost === true;
};

/** Engine: RequestNextRound requires non-Finished phase + actor is a member.
 *  Practically the CTA only shows at end-of-round screens, but the guard is broad. */
export const canRequestNextRound = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase === Phase.Finished) return false;
  return findMe(room, playerId) !== null;
};

/** Engine: PromoteHost requires actor is host + target ≠ self + target is in room.
 *  Server tolerates auto-promotion from system but UI only fires manual. */
export const canPromoteHost = (
  room: Room | null,
  playerId: string | null,
  targetId: string,
): boolean => {
  if (!room) return false;
  const me = findMe(room, playerId);
  if (!me?.isHost) return false;
  if (me.id === targetId) return false;
  return room.players.some((p) => p.id === targetId);
};

/** Engine: UpdateRoomOptions requires host + Lobby. */
export const canUpdateOptions = (room: Room | null, playerId: string | null): boolean => {
  if (!room) return false;
  if (room.phase !== Phase.Lobby) return false;
  const me = findMe(room, playerId);
  return me?.isHost === true;
};
